/**
 * Preferences → "Fueling" section — THE single editor for athlete-level
 * fueling (fueling-profile plan D5/P2.2): sweat sodium, gut level, the one
 * gut-training ramp (status + started date + jump-ahead), and per-sport carb
 * targets. Race-plan settings keep only event-scoped things (race-day target,
 * cadence) and link here instead of duplicating these controls.
 *
 * State model mirrors the old settings editor: `null` = untouched (nothing
 * sent → no spurious regen server-side); the parent modal calls `buildPatch()`
 * on save and only PATCHes when `dirty`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getFuelingProfile,
  type FuelingProfilePatch,
  type FuelingProfileView,
  type GutLevel
} from "../../lib/fuelingProfileApi";
import { isAbortError } from "../../lib/api";
import { addDaysYmd, localTodayYmd } from "../../lib/dates";
import {
  clampCarbTarget,
  clampSodiumTarget,
  SODIUM_TARGET_MAX,
  SODIUM_TARGET_MIN
} from "../../lib/fuelClamps";
import { GUT_QUESTION, SWEAT_QUESTION } from "../../lib/questionCopy";
import { useActivePolicy } from "../../hooks/useActivePolicy";
import "../JourneyRail/journeyRail.css";
// Wizard option-card / choice-card chrome (`.fgp-wz-*`) — the Preferences
// fueling section renders the SAME descriptive cards as the wizard's
// About-you/Fuel steps so the two surfaces read as one profile editor.
import "../Planner/planner.css";

/**
 * String-backed numeric input with clamp-on-commit (P2 input polish, Lane 5 §6).
 * The old inputs coerced `Number(e.target.value)` on every keystroke, so an
 * empty field snapped to 0 mid-edit and an out-of-band value was clamped
 * silently on save. This keeps the raw string while typing, clamps on blur (or
 * Enter), pushes the clamped number to the parent, and shows a brief note when
 * the typed value was truncated so the change isn't invisible.
 */
function ClampedNumberField({
  value,
  clamp,
  onCommit,
  disabled,
  ariaLabel,
  min,
  max,
  step,
  unitLabel,
  className
}: {
  /** Canonical numeric value from the parent (0 = unset). */
  value: number;
  /** Clamp fn (0 stays 0 = unset). */
  clamp: (n: number) => number;
  /** Called with the clamped number on commit. */
  onCommit: (n: number) => void;
  disabled?: boolean;
  ariaLabel: string;
  min: number;
  max: number;
  step: number;
  unitLabel: string;
  className?: string;
}) {
  const [text, setText] = useState<string>(value > 0 ? String(value) : "");
  const [focused, setFocused] = useState(false);
  const [truncated, setTruncated] = useState(false);

  // Reflect external changes (e.g. a sodium proxy button, or a saved-view
  // rehydrate) while the field isn't being edited.
  useEffect(() => {
    if (!focused) setText(value > 0 ? String(value) : "");
  }, [value, focused]);

  const commit = () => {
    const raw = Number(text) || 0;
    const clamped = clamp(raw);
    setText(clamped > 0 ? String(clamped) : "");
    setTruncated(raw > 0 && clamped !== raw);
    // Only notify when the committed value actually differs from what the parent
    // already holds — a bare focus/blur must NOT dirty the form (keeps the
    // "untouched → clean → patches nothing" invariant the parent relies on).
    if (clamped !== value) onCommit(clamped);
  };

  return (
    <>
      <input
        type="number"
        min={min}
        max={max}
        step={step}
        value={text}
        disabled={disabled}
        aria-label={ariaLabel}
        className={className}
        onChange={(e) => {
          setText(e.target.value);
          setTruncated(false);
        }}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
        onFocus={() => setFocused(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
      />
      {truncated && (
        <span
          role="status"
          style={{ fontSize: 12, color: "var(--fg-gray-700)", fontWeight: 600 }}>
          Adjusted to {clamp(Number(text) || 0)} {unitLabel}
        </span>
      )}
    </>
  );
}

/** Sweat-level sodium proxies — W-14: labels + descriptions come from the ONE
 *  shared question table (`lib/questionCopy`), so this surface can never drift
 *  from the wizard's wording. Only the value→mg mapping lives here. The
 *  wizard's extra "Not sure" choice is deliberately absent: in Preferences the
 *  athlete already holds a sodium value, so there is no "pick nothing" state
 *  for a proxy button to express. */
const SWEAT_MG: Record<string, number> = { lightly: 350, somewhat: 525, pretzel: 700 };
const SWEAT_OPTIONS = SWEAT_QUESTION.options
  .filter((o) => o.value in SWEAT_MG)
  .map((o) => ({ label: o.title, desc: o.desc, mg: SWEAT_MG[o.value]! }));

/** Gut-practice options — W-14: rendered straight from `GUT_QUESTION.options`
 *  (values are the `GutLevel` union). */
const GUT_OPTIONS = GUT_QUESTION.options.map((o) => ({
  key: o.value,
  label: o.title,
  desc: o.desc
}));

/** Ramp-duration UI band (server accepts 1-52; we surface a sensible 4-20). */
const RAMP_WEEKS_MIN = 4;
const RAMP_WEEKS_MAX = 20;
const RAMP_WEEKS_DEFAULT = 10;
function clampRampWeeks(n: number): number {
  if (!Number.isFinite(n)) return RAMP_WEEKS_DEFAULT;
  return Math.min(RAMP_WEEKS_MAX, Math.max(RAMP_WEEKS_MIN, Math.round(n)));
}

function fmtShort(ymd: string): string {
  return new Date(`${ymd}T00:00:00Z`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  });
}

// Local calendar day, NOT UTC — `toISOString()` shifts the date for anyone
// east/west of UTC in the evening/morning, skewing the displayed ramp week.
const todayYmd = () => localTodayYmd();

export interface FuelingProfileForm {
  loading: boolean;
  error: string | null;
  view: FuelingProfileView | null;
  dirty: boolean;
  buildPatch: () => FuelingProfilePatch;
  /** Hydrate from the PATCH response so a reopened section shows saved truth. */
  applyView: (view: FuelingProfileView) => void;
  // controls
  gutLevel: GutLevel | null;
  setGutLevel: (g: GutLevel) => void;
  sodiumShown: number;
  pickSodiumProxy: (mg: number) => void;
  setSodiumExact: (mg: number) => void;
  rampEnabledShown: boolean;
  setRampEnabled: (on: boolean) => void;
  rampWeeksShown: number;
  setRampWeeks: (n: number) => void;
  /** The week the athlete picked from the "Current week" dropdown; null =
   *  untouched (stays on the server-computed week). */
  rampWeekTarget: number | null;
  setRampWeekTarget: (week: number) => void;
  startedShown: string;
  setStartedEdit: (ymd: string | null) => void;
  runShown: number;
  setRun: (n: number) => void;
  rideShown: number;
  setRide: (n: number) => void;
}

export function useFuelingProfileForm(): FuelingProfileForm {
  const [view, setView] = useState<FuelingProfileView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // null = untouched — presence is intent, mirroring the old settings editor.
  const [gutEdit, setGutEdit] = useState<GutLevel | null>(null);
  const [sodiumEdit, setSodiumEdit] = useState<number | null>(null);
  const [sodiumSourceEdit, setSodiumSourceEdit] = useState<"explicit" | "proxy" | null>(null);
  const [rampEnabledEdit, setRampEnabledEdit] = useState<boolean | null>(null);
  const [rampWeeksEdit, setRampWeeksEdit] = useState<number | null>(null);
  const [rampWeekTarget, setRampWeekTarget] = useState<number | null>(null);
  const [startedEdit, setStartedEditState] = useState<string | null>(null);
  const [runEdit, setRunEdit] = useState<number | null>(null);
  const [rideEdit, setRideEdit] = useState<number | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    getFuelingProfile(ctrl.signal)
      .then((v) => {
        if (!ctrl.signal.aborted) setView(v);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted || isAbortError(err)) return;
        setError("Couldn't load your fueling profile");
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, []);

  const profile = view?.profile ?? null;
  const cfg = profile?.rampConfig ?? null;
  const gutLevel = gutEdit ?? (profile?.gutLevel as GutLevel | null) ?? null;
  const sodiumShown = sodiumEdit ?? profile?.sodiumTarget ?? 0;
  const rampEnabledShown = rampEnabledEdit ?? profile?.rampEnabled ?? false;
  const rampWeeksShown = rampWeeksEdit ?? cfg?.weeks ?? RAMP_WEEKS_DEFAULT;
  const startedShown = startedEdit ?? cfg?.startedYmd ?? todayYmd();
  const runShown = runEdit ?? profile?.perSportTargets?.run ?? 0;
  const rideShown = rideEdit ?? profile?.perSportTargets?.ride ?? 0;

  const weeksChanged = rampWeeksEdit != null && rampWeeksEdit !== (cfg?.weeks ?? null);
  // The server-computed current week (includes any saved jumpWeeks). The week
  // dropdown is a no-op when it lands back on this value.
  const serverWeekNow = view?.ramp?.weekNow ?? null;
  const weekTargetChanged = rampWeekTarget != null && rampWeekTarget !== serverWeekNow;

  const dirty =
    gutEdit != null ||
    sodiumEdit != null ||
    rampEnabledEdit != null ||
    weekTargetChanged ||
    (startedEdit != null && startedEdit !== (cfg?.startedYmd ?? null)) ||
    weeksChanged ||
    runEdit != null ||
    rideEdit != null;

  const buildPatch = useCallback((): FuelingProfilePatch => {
    const patch: FuelingProfilePatch = {};
    if (gutEdit != null) patch.gutLevel = gutEdit;
    if (sodiumEdit != null) {
      // F-16-2: the PATCH schema floors sodium at 100 mg/hr; a cleared field
      // (0 = unset) is sent as `null` — the schema's clear value — never as 0.
      const mg = clampSodiumTarget(sodiumEdit);
      patch.sodiumTarget = mg > 0 ? mg : null;
      patch.sodiumSource = sodiumSourceEdit ?? "explicit";
    }
    if (runEdit != null || rideEdit != null) {
      // Merge-on-save: keep any per-sport keys set elsewhere; drop zeros.
      const merged: Record<string, number> = { ...(profile?.perSportTargets ?? {}) };
      const run = clampCarbTarget(runEdit ?? runShown);
      const ride = clampCarbTarget(rideEdit ?? rideShown);
      if (run > 0) merged.run = run;
      else delete merged.run;
      if (ride > 0) merged.ride = ride;
      else delete merged.ride;
      patch.perSportTargets = Object.keys(merged).length > 0 ? merged : null;
    }
    const enabling = rampEnabledEdit === true;
    const startedChanged = startedEdit != null && startedEdit !== (cfg?.startedYmd ?? null);
    const weeksNow = clampRampWeeks(rampWeeksEdit ?? cfg?.weeks ?? RAMP_WEEKS_DEFAULT);
    if (rampEnabledEdit != null) patch.rampEnabled = rampEnabledEdit;
    if (enabling && !cfg) {
      // First enable anchors at today (plan Q1) unless the athlete picked a
      // date; duration comes from the athlete-set field (A3-UI) not a hardcode.
      patch.rampConfig = { startPct: 46, weeks: weeksNow, startedYmd: startedEdit ?? todayYmd() };
    } else if (cfg && (weekTargetChanged || startedChanged || weeksChanged)) {
      // "Current week" dropdown: moving FORWARD stays an explicit jumpWeeks
      // offset (audit F1 — the anchor date is history, don't rewrite it);
      // moving BACKWARD (or from a pending ramp) can't be expressed as a
      // non-negative offset, so we re-anchor: startedYmd = today − (W−1) weeks
      // with the jump cleared, which lands the ramp on exactly week W.
      let startedYmd = startedEdit ?? cfg.startedYmd;
      let jumpWeeks = cfg.jumpWeeks ?? 0;
      if (weekTargetChanged && rampWeekTarget != null) {
        const delta = rampWeekTarget - (serverWeekNow ?? 0);
        if (delta > 0 && serverWeekNow != null && serverWeekNow >= 1) {
          jumpWeeks += delta;
        } else {
          startedYmd = addDaysYmd(todayYmd(), -(rampWeekTarget - 1) * 7);
          jumpWeeks = 0;
        }
      }
      patch.rampConfig = {
        startPct: cfg.startPct,
        weeks: weeksNow,
        startedYmd,
        jumpWeeks
      };
    }
    return patch;
  }, [
    gutEdit,
    sodiumEdit,
    sodiumSourceEdit,
    runEdit,
    rideEdit,
    rampEnabledEdit,
    rampWeeksEdit,
    weeksChanged,
    rampWeekTarget,
    weekTargetChanged,
    serverWeekNow,
    startedEdit,
    cfg,
    profile,
    runShown,
    rideShown
  ]);

  const applyView = useCallback((v: FuelingProfileView) => {
    setView(v);
    setGutEdit(null);
    setSodiumEdit(null);
    setSodiumSourceEdit(null);
    setRampEnabledEdit(null);
    setRampWeeksEdit(null);
    setRampWeekTarget(null);
    setStartedEditState(null);
    setRunEdit(null);
    setRideEdit(null);
  }, []);

  return useMemo(
    () => ({
      loading,
      error,
      view,
      dirty,
      buildPatch,
      applyView,
      gutLevel,
      setGutLevel: setGutEdit,
      sodiumShown,
      pickSodiumProxy: (mg: number) => {
        setSodiumEdit(mg);
        setSodiumSourceEdit("proxy");
      },
      setSodiumExact: (mg: number) => {
        setSodiumEdit(clampSodiumTarget(mg || 0));
        setSodiumSourceEdit("explicit");
      },
      rampEnabledShown,
      setRampEnabled: setRampEnabledEdit,
      rampWeeksShown,
      setRampWeeks: (n: number) => setRampWeeksEdit(clampRampWeeks(n)),
      rampWeekTarget,
      setRampWeekTarget,
      startedShown,
      setStartedEdit: setStartedEditState,
      runShown,
      setRun: setRunEdit,
      rideShown,
      setRide: setRideEdit
    }),
    [
      loading,
      error,
      view,
      dirty,
      buildPatch,
      applyView,
      gutLevel,
      sodiumShown,
      rampEnabledShown,
      rampWeeksShown,
      rampWeekTarget,
      startedShown,
      runShown,
      rideShown
    ]
  );
}

export function FuelingSection({
  form,
  disabled,
  onOpenPlanSettings
}: {
  form: FuelingProfileForm;
  disabled?: boolean;
  /** R8: with active race plans, the race-day-target footnote opens that plan's
   *  settings directly (was a blind /plans navigation — My Training UX fix §3).
   *  With multiple plans the parent routes to the soonest journey's settings. */
  onOpenPlanSettings?: () => void;
}) {
  const ramp = form.view?.ramp ?? null;
  // Phase 3c: the ramp-week fallback comes from the active policy
  // (`workoutRules.ramp.defaultWeeks`), not a hardcoded 10. Only used when the
  // server hasn't sent a computed position yet; the constant is the last resort
  // while the policy request is in flight / unavailable.
  const activePolicy = useActivePolicy();
  const policyDefaultWeeks = activePolicy?.workoutRules.ramp.defaultWeeks ?? 10;
  // Server-computed position (F2/D9) — this section never re-derives weeks.
  const rampWeeksTotal = ramp?.weeksTotal ?? policyDefaultWeeks;
  const rampWeekNow = ramp?.weekNow ?? 1;
  const rampWeekShown = form.rampWeekTarget ?? rampWeekNow;
  const weekMoved = form.rampWeekTarget != null && form.rampWeekTarget !== rampWeekNow;
  // Once the ramp finishes the server auto-disables it (A2). Reflect that as a
  // completion note + OFF toggle rather than a live "week N of M" counter.
  const rampCompleted = ramp?.state === "completed";
  const rampToggleChecked = form.rampEnabledShown && !rampCompleted;
  // "How your targets resolve" is a show/hide link, not always-open chrome.
  const [showResolve, setShowResolve] = useState(false);

  const rampStatus = (() => {
    if (rampCompleted) {
      return ramp?.completedYmd
        ? `Ramp completed ${fmtShort(ramp.completedYmd)} — you're at full fuel.`
        : "Ramp completed — you're at full fuel.";
    }
    if (!form.rampEnabledShown) return "Off — full carb targets from day one.";
    if (!ramp) return "Builds your carbs up gradually — week 1 starts gentle.";
    if (weekMoved) {
      return `Gut-training week ${rampWeekNow} of ${rampWeeksTotal} — will move to week ${rampWeekShown} on save`;
    }
    if (ramp.state === "pending") return `Starts ${fmtShort(form.startedShown)}`;
    if (ramp.state === "full") return "At full fuel — the build-up is done.";
    return `Gut-training week ${rampWeekNow} of ${rampWeeksTotal} — full fuel by ${fmtShort(ramp.fullYmd)}`;
  })();

  if (form.loading) {
    return <div className="fgjs"><div className="fgjs__row-sub">Loading your fueling profile…</div></div>;
  }
  if (form.error) {
    return <div className="fgjs"><div className="fgjs__row-sub">{form.error}</div></div>;
  }

  return (
    <div className="fgjs fgjs--profile">
      {/* ── Sweat / sodium ─────────────────────────────────────────────── */}
      <div className="fgp-wz-field">
        <div id="fgjs-sweat-label" className="fgp-wz-fuel__label">
          {SWEAT_QUESTION.label}
        </div>
        <p className="fgp-wz-fuel__desc">{SWEAT_QUESTION.desc}</p>
        <div
          className="fgp-wz-choices fgp-wz-choices--stack-mobile"
          role="radiogroup"
          aria-labelledby="fgjs-sweat-label">
          {SWEAT_OPTIONS.map((o) => (
            <button
              key={o.mg}
              type="button"
              role="radio"
              aria-checked={form.sodiumShown === o.mg}
              disabled={disabled}
              className={`fgp-wz-choice${form.sodiumShown === o.mg ? " fgp-wz-choice--on" : ""}`}
              onClick={() => form.pickSodiumProxy(o.mg)}>
              <span className="fgp-wz-choice__t">{o.label}</span>
              <span className="fgp-wz-choice__s">{o.desc}</span>
            </button>
          ))}
        </div>
        <div className="fgjs__targets">
          <label className="fgjs__target">
            <span className="fgjs__target-label">Or exact (mg/hr)</span>
            <ClampedNumberField
              value={form.sodiumShown}
              clamp={clampSodiumTarget}
              onCommit={form.setSodiumExact}
              disabled={disabled}
              ariaLabel="Sodium target mg per hour"
              min={SODIUM_TARGET_MIN}
              max={SODIUM_TARGET_MAX}
              step={25}
              unitLabel="mg/hr"
            />
          </label>
        </div>
      </div>

      {/* ── Gut practice ───────────────────────────────────────────────── */}
      <div className="fgp-wz-field">
        <div id="fgjs-gut-label" className="fgp-wz-fuel__label">
          {GUT_QUESTION.label}
        </div>
        <p className="fgp-wz-fuel__desc">{GUT_QUESTION.desc}</p>
        <div
          className="fgp-wz-choices"
          style={{ flexDirection: "column" }}
          role="radiogroup"
          aria-labelledby="fgjs-gut-label">
          {GUT_OPTIONS.map((g) => (
            <button
              key={g.key}
              type="button"
              role="radio"
              aria-checked={form.gutLevel === g.key}
              disabled={disabled}
              className={`fgp-wz-choice${form.gutLevel === g.key ? " fgp-wz-choice--on" : ""}`}
              onClick={() => form.setGutLevel(g.key)}>
              <span className="fgp-wz-choice__t">{g.label}</span>
              <span className="fgp-wz-choice__s">{g.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Gut-training ramp — one card for every ramp setting. Lavender
          card while the ramp is on; a bare toggle row when it's off
          (`--off` strips the bg/border/padding). ─────────────────────── */}
      <div className={`fgp-wz-optcard${rampToggleChecked ? " fgp-wz-optcard--on" : " fgp-wz-optcard--off"}`}>
        <div className="fgp-wz-optcard__head">
          <div className="fgp-wz-optcard__title">
            Gut-training ramp
            <div className="fgjs__row-sub">{rampStatus}</div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={rampToggleChecked}
            aria-label="Gut-training ramp"
            aria-disabled={disabled || rampCompleted ? "true" : undefined}
            className={`fgp-wz-switch${rampToggleChecked ? " fgp-wz-switch--on" : ""}`}
            onClick={() => {
              if (disabled || rampCompleted) return;
              form.setRampEnabled(!rampToggleChecked);
            }}>
            <span />
          </button>
        </div>
        {rampToggleChecked && (
          <div className="fgp-wz-optcard__body">
            {ramp?.compressedForRace && (
              <p className="fgp-wz-optcard__copy">
                Finishing by {fmtShort(ramp.fullYmd)} so you&apos;re race-ready
                {ramp.compressedForRace.raceName
                  ? ` for ${ramp.compressedForRace.raceName}`
                  : ` for your race on ${fmtShort(ramp.compressedForRace.raceYmd)}`}
                .
              </p>
            )}
            {ramp?.warning && (
              <p
                className="fgp-wz-optcard__copy"
                role="status"
                style={{ color: "var(--fg-red, #C4320A)", fontWeight: 600 }}>
                ⚠ {ramp.warning}
              </p>
            )}
            {ramp && !rampCompleted && ramp.state !== "full" && (
              <div className="fgjs__row">
                <div className="fgjs__row-main">
                  <div className="fgjs__row-title">Current week</div>
                  <div className="fgjs__row-sub">
                    Further along (or behind) than we think? Pick the week that matches your gut.
                  </div>
                </div>
                <select
                  className="fgjs__dateinput"
                  aria-label="Current gut-training week"
                  value={rampWeekShown}
                  disabled={disabled}
                  onChange={(e) => form.setRampWeekTarget(Number(e.target.value))}>
                  {Array.from({ length: rampWeeksTotal }, (_, i) => i + 1).map((w) => (
                    <option key={w} value={w}>
                      Week {w} of {rampWeeksTotal}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="fgjs__row">
              <div className="fgjs__row-main">
                <div className="fgjs__row-title">Started</div>
                <div className="fgjs__row-sub">
                  When you began building your carbs — one ramp, shared by every race plan.
                </div>
              </div>
              <input
                type="date"
                className="fgjs__dateinput"
                aria-label="Gut-training start date"
                value={form.startedShown}
                disabled={disabled}
                onChange={(e) => form.setStartedEdit(e.target.value || null)}
              />
            </div>
            <div className="fgjs__row">
              <div className="fgjs__row-main">
                <div className="fgjs__row-title">Ramp duration (weeks)</div>
                <div className="fgjs__row-sub">
                  How long the build-up runs.{" "}
                  {ramp?.fullYmd
                    ? `Full fuel by ${fmtShort(ramp.fullYmd)}.`
                    : "We'll show the full-fuel date once you save."}
                </div>
              </div>
              <input
                type="number"
                className="fgjs__dateinput"
                min={RAMP_WEEKS_MIN}
                max={RAMP_WEEKS_MAX}
                step={1}
                value={form.rampWeeksShown}
                disabled={disabled}
                aria-label="Ramp duration in weeks"
                onChange={(e) => form.setRampWeeks(Number(e.target.value))}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Everyday carb targets ──────────────────────────────────────── */}
      <div className="fgp-wz-field">
        <div className="fgp-wz-fuel__label">
          Everyday carb targets (g/hr){" "}
          <span className="fgp-wz-fuel__label-hint">— optional</span>
        </div>
        <p className="fgp-wz-fuel__desc">
          Your top-end targets for a big, hard day — easier or shorter sessions scale down
          from them.
        </p>
        <div className="fgjs__targets">
          <label className="fgjs__target">
            <span className="fgjs__target-label">Runs</span>
            <ClampedNumberField
              value={form.runShown}
              clamp={clampCarbTarget}
              onCommit={form.setRun}
              disabled={disabled}
              ariaLabel="Runs carbs per hour"
              min={0}
              max={140}
              step={5}
              unitLabel="g/hr"
            />
          </label>
          <label className="fgjs__target">
            <span className="fgjs__target-label">Rides</span>
            <ClampedNumberField
              value={form.rideShown}
              clamp={clampCarbTarget}
              onCommit={form.setRide}
              disabled={disabled}
              ariaLabel="Rides carbs per hour"
              min={0}
              max={140}
              step={5}
              unitLabel="g/hr"
            />
          </label>
        </div>
        <div className="fgp-modal-footnote">
          Leave at 0 to let us pick from your plan. Race-day targets live in each race
          plan&apos;s settings
          {onOpenPlanSettings ? (
            <>
              {" — "}
              <button
                type="button"
                className="fgjs__row-action fgjs__row-action--inline"
                onClick={onOpenPlanSettings}>
                open plan settings →
              </button>
            </>
          ) : (
            "."
          )}
        </div>
      </div>

      {/* ── How your targets resolve — show/hide link ──────────────────── */}
      {!showResolve ? (
        <button type="button" className="fgp-wz-ramplink" onClick={() => setShowResolve(true)}>
          How your targets resolve →
        </button>
      ) : (
        <div className="fgjs__resolve">
          <div
            style={{
              fontSize: 13,
              color: "var(--fg-gray-700)",
              lineHeight: 1.5
            }}>
            <p style={{ margin: "0 0 8px", fontFamily: "var(--fg-display)", fontWeight: 700, color: "var(--fg-ink-strong)" }}>
              How your targets resolve
            </p>
            <p style={{ margin: "0 0 8px" }}>
              For any workout we use the most specific number we have, in order:
            </p>
            <ol style={{ margin: "0 0 8px", paddingLeft: 18 }}>
              <li style={{ marginBottom: 2 }}>A per-workout override you set on that day</li>
              <li style={{ marginBottom: 2 }}>Your race-day target for that plan</li>
              <li style={{ marginBottom: 2 }}>Your per-sport number above (runs / rides)</li>
              <li style={{ marginBottom: 2 }}>Our policy, sized from your body &amp; the workout</li>
            </ol>
            <p style={{ margin: "0 0 8px" }}>
              Think of your per-sport and race-day numbers as your <strong>top-end target</strong> —
              what a big, hard day earns. Easier or shorter days scale down from there, so it&apos;s a
              ceiling to reach for, not a floor we force onto every session.
            </p>
            <p style={{ margin: 0 }}>
              While the gut ramp is running it scales your during-fuel down as you build up, then eases
              off to your full targets by the end.
            </p>
          </div>
          <button type="button" className="fgp-wz-ramplink" onClick={() => setShowResolve(false)}>
            Hide
          </button>
        </div>
      )}
    </div>
  );
}
